/**
 * "Performance" tab of the MARS portfolio dashboard.
 *
 * A period selector (with a custom date-range mode) above a backtest
 * performance view: a full-width cumulative chart, an optional
 * tactical/drawdown 2-up grid for benchmark funds, a metrics table, and —
 * when the payload carries them — an asset-class breakdown (the
 * `AssetClassBreakdown` sub-component at the bottom of this file).
 *
 * Plotly figures are built by pure builders in `../lib/performanceCharts`;
 * this file only wires data to selectors. Fidelity funds default to the
 * 5-year period, every other fund to since-inception.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { fetchPerformance, marsPortfolioKeys } from '../api';
import { ChartCard } from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import SegmentedControl from '@/components/SegmentedControl';
import { SectionHeader } from './sectionUi';
import { useMarsPortfolioStore } from '../store';
import { PerformanceTable, AssetClassTable } from './PerformanceTables';
import {
  buildAssetClassPlots,
  buildPerformancePlots,
  type AssetClassBuiltPlots,
} from '../lib/performanceCharts';
import type { Fund, PerformancePeriod, PerformancePeriodData } from '../types';

interface PerformanceSectionProps {
  fund: Fund;
}

const PERIOD_OPTIONS: ReadonlyArray<{ value: PerformancePeriod; label: string }> = [
  { value: 'since_inception', label: 'Since Inception' },
  { value: '1_year', label: '1 Year' },
  { value: '3_years', label: '3 Years' },
  { value: '5_years', label: '5 Years' },
  { value: 'ytd', label: 'YTD' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Short fund names ("QMAS", "FQM") shown in the description prose, rather
 * than the long `display_name`.
 */
function fundShortName(fundId: string): string {
  switch (fundId) {
    case 'mars-qmas':
      return 'QMAS';
    case 'mars-qmas-nn':
      return 'QMAS NN';
    case 'mars-dar-nn':
      return 'DAR NN';
    case 'mars-dar':
      return 'DAR';
    case 'mat-ucits':
      return 'MAT UCITS';
    case 'mars-fqm':
      return 'FQM';
    case 'mars-fidelity-balanced':
      return 'Balanced Portfolio';
    case 'mars-fidelity-conservative':
      return 'Conservative Portfolio';
    default:
      return 'selected portfolio';
  }
}

/** Fidelity funds default to '5_years', all others to 'since_inception'. */
function getDefaultPerformancePeriod(fundId: string): PerformancePeriod {
  if (fundId === 'mars-fidelity-balanced' || fundId === 'mars-fidelity-conservative') {
    return '5_years';
  }
  return 'since_inception';
}

export default function PerformanceSection({ fund }: PerformanceSectionProps): JSX.Element {
  const performancePeriod = useMarsPortfolioStore((s) => s.performancePeriod);
  const setPerformancePeriod = useMarsPortfolioStore((s) => s.setPerformancePeriod);
  const customStart = useMarsPortfolioStore((s) => s.customStartDate);
  const customEnd = useMarsPortfolioStore((s) => s.customEndDate);
  const setCustomDates = useMarsPortfolioStore((s) => s.setCustomDates);

  const [localStart, setLocalStart] = useState(customStart ?? '');
  const [localEnd, setLocalEnd] = useState(customEnd ?? '');

  // Set the fund-specific default period once on first mount.
  const initialised = useRef(false);
  useEffect(() => {
    if (!initialised.current) {
      initialised.current = true;
      setPerformancePeriod(getDefaultPerformancePeriod(fund.id));
    }
  }, [fund.id, setPerformancePeriod]);

  const isCustom = performancePeriod === 'custom';
  const enabled = !isCustom || (!!customStart && !!customEnd);

  const query = useQuery({
    queryKey: marsPortfolioKeys.performance(
      fund.id,
      customStart ?? undefined,
      customEnd ?? undefined,
    ),
    queryFn: () =>
      fetchPerformance(
        fund.id,
        isCustom ? (customStart ?? undefined) : undefined,
        isCustom ? (customEnd ?? undefined) : undefined,
      ),
    enabled,
  });

  const periodData: PerformancePeriodData | undefined = useMemo(() => {
    if (!query.data?.success) return undefined;
    if (isCustom) return query.data.data.periods.custom;
    return query.data.data.periods[performancePeriod];
  }, [query.data, performancePeriod, isCustom]);

  const plots = useMemo(
    () => buildPerformancePlots(periodData, fund.has_benchmark),
    [periodData, fund.has_benchmark],
  );

  const assetClassPlots = useMemo(
    () => buildAssetClassPlots(periodData, fund.has_benchmark),
    [periodData, fund.has_benchmark],
  );

  const shortName = fundShortName(fund.id);
  const hasAssetClasses =
    periodData?.asset_classes != null && Object.keys(periodData.asset_classes).length > 0;

  return (
    <section>
      <div className="px-4">
        <SectionHeader
          title="Performance"
          description={
            <>
              This page presents the historical backtest performance of MARS within the {shortName}.
              Using historical portfolio positions, returns are computed from broad asset-class
              baskets with indicative transaction costs applied. The charts report total and active
              returns alongside summary risk-adjusted metrics at the portfolio level. The view also
              decomposes performance into asset-class sleeves, providing clear insight into which
              segments of the portfolio have driven results over time.
            </>
          }
        />

        <div className="mb-4">
          <SegmentedControl
            variant="group-secondary"
            label="Time Period:"
            options={PERIOD_OPTIONS}
            value={performancePeriod}
            onChange={setPerformancePeriod}
          />
        </div>

        {isCustom ? (
          <div className="flex items-center gap-3 bg-[color:var(--light-bg)] border border-gray-300 rounded-lg p-3 mb-4">
            <label className="text-sm text-[color:var(--text-dark)]">Start Date:</label>
            <input
              type="date"
              value={localStart}
              onChange={(e) => setLocalStart(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            />
            <label className="text-sm text-[color:var(--text-dark)]">End Date:</label>
            <input
              type="date"
              value={localEnd}
              onChange={(e) => setLocalEnd(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            />
            <button
              type="button"
              onClick={() => {
                if (!localStart || !localEnd) return;
                if (new Date(localStart) > new Date(localEnd)) return;
                setCustomDates(localStart, localEnd);
              }}
              className="px-4 py-1 bg-slate-600 text-white rounded text-sm"
            >
              Apply
            </button>
          </div>
        ) : null}
      </div>

      <div className="px-4 mb-6">
        {/* Cumulative chart (full width). */}
        <ChartCard height={450} className="mb-4">
          <QueryState
            query={{ ...query, data: plots.cumulative.data }}
            isEmpty={(d) => d.length === 0}
            loadingLabel="Loading"
            errorLabel="No data available"
            emptyLabel="No data available"
          >
            {(data) => <PlotlyChart data={data} layout={plots.cumulative.layout} />}
          </QueryState>
        </ChartCard>

        {/* Tactical + Drawdown 2-up grid. */}
        {fund.has_benchmark ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ChartCard height={350}>
              {plots.tactical.data.length > 0 ? (
                <PlotlyChart data={plots.tactical.data} layout={plots.tactical.layout} />
              ) : null}
            </ChartCard>
            <ChartCard height={350}>
              {plots.drawdown.data.length > 0 ? (
                <PlotlyChart data={plots.drawdown.data} layout={plots.drawdown.layout} />
              ) : null}
            </ChartCard>
          </div>
        ) : null}

        {/* Performance metrics table. */}
        <PerformanceTable data={periodData} hasBenchmark={fund.has_benchmark} />
        <p className="mt-3 text-xs text-gray-500 text-center font-[var(--font-heading)]">
          {periodData?.start_date && periodData?.end_date
            ? `Performance period: ${periodData.start_date} to ${periodData.end_date}`
            : query.isLoading
              ? 'Loading performance data...'
              : 'No performance data available.'}
        </p>

        {/* Asset Class Breakdown — subtitle, per-asset table, and 2-up
          chart grid. */}
        {hasAssetClasses ? (
          <AssetClassBreakdown
            data={periodData!}
            hasBenchmark={fund.has_benchmark}
            plots={assetClassPlots}
          />
        ) : null}
      </div>
    </section>
  );
}

interface AssetClassBreakdownProps {
  data: PerformancePeriodData;
  hasBenchmark: boolean;
  plots: AssetClassBuiltPlots;
}

/**
 * Asset Class Breakdown section.
 *
 * Contains:
 *   1. Heading with subtitle for benchmark funds.
 *   2. Per-asset metrics table (Period Return, Active Return, Alpha,
 *      Volatility, Sharpe, Tracking Error).
 *   3. Two-chart grid: Tactical Performance (Active NAV) on the left
 *      (benchmark funds only) and Asset Class Cumulative NAV on the right.
 *      For absolute-only funds the cumulative chart takes full width.
 */
function AssetClassBreakdown({ data, hasBenchmark, plots }: AssetClassBreakdownProps): JSX.Element {
  const assetClasses = data.asset_classes ?? {};

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-[color:var(--text-dark)] mb-3 font-[var(--font-heading)]">
        Asset Class Breakdown
        {hasBenchmark ? (
          <span className="text-sm font-normal text-gray-500 ml-2">
            What&apos;s driving performance vs. benchmark?
          </span>
        ) : null}
      </h3>

      {/* Per-asset metrics table */}
      <AssetClassTable assetClasses={assetClasses} hasBenchmark={hasBenchmark} />

      {/* Charts: 2-up when benchmark, 1-up when absolute-only */}
      <div className={`grid gap-4 ${hasBenchmark ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {hasBenchmark && plots.tactical.data.length > 0 ? (
          <ChartCard height={380}>
            <PlotlyChart data={plots.tactical.data} layout={plots.tactical.layout} />
          </ChartCard>
        ) : null}
        {plots.cumulativeNav.data.length > 0 ? (
          <ChartCard height={380}>
            <PlotlyChart data={plots.cumulativeNav.data} layout={plots.cumulativeNav.layout} />
          </ChartCard>
        ) : null}
      </div>
    </div>
  );
}
